import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { createFragmentContainer, graphql } from 'react-relay';
import { useIntl } from 'react-intl';
import { authenticateUser } from 'src/auth';
import FlashesRenderer from 'src/components/Flashes/FlashesRenderer';
import Footer from 'src/enosikit/components/Footer';
import Loading from 'src/components/Loading';
import CustomerPropertyNavigation from 'src/components/Navigation/CustomerPropertyNavigation';
import { withTracingHOC } from 'src/tracing';
import HTTP404 from 'src/components/HTTP404';

/**
 * Description
 * @param {any} props
 * @returns {React.ReactComponentElement} - CustomerPropertyLayout component
 */
function CustomerPropertyLayout(props) {
  const {
    children, property, router, viewer,
  } = props;

  // Lift datespanStart state to parent to share between sibling components
  const [datespanStart, setDatespanStart] = useState(null);

  const handleDatespanStartChange = useCallback((newDatespanStart) => {
    setDatespanStart(newDatespanStart);
  }, []);

  if (!property) {
    return <HTTP404 />;
  }
  const intl = useIntl();

  if (!authenticateUser(intl, viewer, null, router)) {
    return <Loading />;
  }

  const { title: propertyTitle } = property;
  const pageTitle = intl.formatMessage({ id: 'layout.customer_layout.customer_property_layout.page.title', defaultMessage: 'Enosi - Property: {propertyTitle}' }, { propertyTitle });

  // Clone children and inject datespanStart and onDatespanStartChange props
  const childrenWithProps = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, {
        datespanStart,
        onDatespanStartChange: handleDatespanStartChange,
      });
    }
    return child;
  });

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      <div className="flex flex-column" style={{ minHeight: '80vh' }}>
        <CustomerPropertyNavigation
          viewer={viewer}
          property={property}
          datespanStart={datespanStart}
        />
        <section id="main">
          <FlashesRenderer />
          <div className="container">
            <div className="flex-auto">{childrenWithProps}</div>
          </div>
        </section>
      </div>

      <Footer versionUI={VERSION} />
    </>
  );
}

CustomerPropertyLayout.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  property: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
  }),
  router: PropTypes.shape({
    replace: PropTypes.func,
  }).isRequired,
  viewer: PropTypes.shape({
    id: PropTypes.string,
    properties: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  }),
};

CustomerPropertyLayout.defaultProps = {
  property: null,
  viewer: null,
};

export default withTracingHOC('CustomerPropertyLayout', createFragmentContainer(CustomerPropertyLayout, {
  viewer: graphql`
    fragment CustomerPropertyLayout_viewer on Viewer {
      id
      viewerUser {
        id
        type
      }
      ...CustomerPropertyNavigation_viewer
      ...CustomerPropertyNavigation_properties
    }
  `,
  property: graphql`
    fragment CustomerPropertyLayout_property on Property {
      title
      ...CustomerPropertyNavigation_property
    }
  `,
}));
